/**
 * Whether the member's plan pays for THIS booking — not whether they hold one.
 *
 * A bay can be left out of plan coverage (bay customization): the member keeps
 * their identity (hours, allow-lists, team) but pays what a drop-in pays. The
 * server says so by answering the quote with `subscription: null` — the plan that
 * pays, not the plan the member holds. Reading the member's own plan instead shows
 * "Membership Coverage · Free" over a walk-in charge.
 *
 * Pure, and extracted for the same reason as `bookingQuote.ts`: this decision
 * governs what a member is shown before their card is charged.
 *
 * This answers WHETHER a plan pays. WHICH plan pays is the library's
 * `payerFromQuote`: a pair holder's pass pays first whenever it covers the whole
 * booking (pass-first, subscription unification D13), and the quote names it.
 *
 * @module pages/booking/steps/planCoverage
 */

export interface PlanCoverageInputs {
  /** The member holds an active membership or pass (their own plan, not the quote). */
  hasActiveSubscription: boolean;
  /** A quote has landed. Until then the member's own plan is the best guess. */
  hasQuote: boolean;
  /**
   * The quote's payer plan: `null` when no plan pays here, `undefined` on the guest
   * path (which never carries one) or while unanswered.
   */
  quoteSubscription: unknown;
}

/**
 * A plan pays unless a landed quote explicitly withheld it (`null`) — mirroring the
 * mobile ConfirmBookingPage, so the two apps price an uncovered bay the same way.
 */
export function planPaysHere({ hasActiveSubscription, hasQuote, quoteSubscription }: PlanCoverageInputs): boolean {
  const withheld = hasQuote && quoteSubscription === null;
  return hasActiveSubscription && !withheld;
}
